import { isDevMode, LOCALE_ID, NgModule, inject} from '@angular/core';
import { BrowserModule, provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { MultiTranslateHttpLoader } from '@ekowitryna/ui';
import { providePrimeNG } from 'primeng/config';
import { SharedModule } from './shared/shared.module';
import { environment } from '../environments/environment';
import {
  provideKeycloak,
  withAutoRefreshToken,
  AutoRefreshTokenService,
  UserActivityService,
  includeBearerTokenInterceptor,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  createInterceptorCondition,
  IncludeBearerTokenCondition
} from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { StoreModule } from '@ngrx/store';
import { ApiModule as AuthServiceApiModule, Configuration as AuthServiceConfiguration, ConfigurationParameters as AuthServiceConfigurationParameters } from '@mobiletract/auth-service-angular-client';
import { ApiModule as AccountServiceApiModule, Configuration as AccountServiceConfiguration, ConfigurationParameters as AccountServiceConfigurationParameters } from '@mobiletract/account-service-angular-client';
import { CompanyContextEffects } from './core/state/company/company-context.effects';
import { UserEffects } from './core/state/user/user.effects';
import { EffectsModule } from '@ngrx/effects';
import { companyContextReducer, localStorageMetaReducer } from './core/state/company/company-context.reducer';
import { userReducer } from './core/state/user/user.reducer';
import { FirstConfigurationModule } from './features/first-configuration/first-configuration.module';
import { ErrorPageModule } from './features/error-page/error-page.module';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { ekowitrynaPreset, provideEkoHostAppMetadata } from '@ekowitryna/ui';
import packageJson from '../../package.json';
import { TelemetryService } from './core/services/telemetry/telemetry.service';
import { APP_INITIALIZER } from '@angular/core';
import { version } from '../../package.json';
import { provideEkoPreferencesSync } from './core/providers/eko-dark-mode.providers';
import { provideEkoBrowserTitle } from '@ekowitryna/ui';
import { GroupCacheInterceptor } from './shared/interceptors/group-cache.interceptor';

export function HttpLoaderFactory(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: './assets/ui/i18n/', suffix: '.json?version=' + version },
    { prefix: './assets/i18n/', suffix: '.json?version=' + version },
  ]);
}

export function authApiConfigFactory(): AuthServiceConfiguration {
  const keycloak = inject(Keycloak);
  return new AuthServiceConfiguration({
    basePath: `${environment.apiUrl.replace(/\/$/, '')}/uaa`,
    credentials: {
      'Bearer JWT Token': () => keycloak.token ?? '',
    },
  });
}

export function accountApiConfigFactory(): AccountServiceConfiguration {
  const params: AccountServiceConfigurationParameters = {
    basePath: `${environment.apiUrl}accounts`,
  };
  return new AccountServiceConfiguration(params);
}

// Configure bearer token interceptor to add Authorization header to all requests
// except bdomobile.json (negative lookahead regex)
const bearerTokenCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: /^(?!.*bdomobile\.json).*$/,  // Match all URLs except bdomobile.json
  bearerPrefix: 'Bearer'
});

function initializeTelemetry(telemetry: TelemetryService) {
  return () => {
    telemetry.initialize();
  };
}

@NgModule({
  declarations: [
    AppComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    SharedModule,
    AppRoutingModule,
    ErrorPageModule,
    FirstConfigurationModule,
    AuthServiceApiModule.forRoot(authApiConfigFactory),
    AccountServiceApiModule.forRoot(accountApiConfigFactory),
    StoreModule.forRoot({}, {
      metaReducers: []
    }),
    StoreModule.forFeature('companyContext', companyContextReducer, {
      metaReducers: [localStorageMetaReducer]
    }),
    StoreModule.forFeature('user', userReducer),
    EffectsModule.forRoot([CompanyContextEffects, UserEffects]),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
  ],

  providers: [
    TelemetryService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTelemetry,
      multi: true,
      deps: [TelemetryService]
    },
    provideAnimations(),
    provideEkoHostAppMetadata({
      name: packageJson.name,
      version: packageJson.version
    }),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([includeBearerTokenInterceptor])
    ),
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [bearerTokenCondition]
    },
    provideKeycloak({
      config: {
        url: environment.keycloakUrl,
        realm: environment.realm,
        clientId: environment.clientId
      },
      initOptions: {
        onLoad: 'check-sso',
        checkLoginIframe: false,
        silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html'
      },
      features: [
        withAutoRefreshToken({ onInactivityTimeout: 'none' })
      ],
      providers: [AutoRefreshTokenService, UserActivityService]
    }),
    {
      provide: AuthServiceConfiguration,
      useFactory: authApiConfigFactory,
    },
    GroupCacheInterceptor,
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: GroupCacheInterceptor,
      multi: true,
    },
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: true // If set to true, the connection is established within the Angular zone
    }),
    {
      provide: LOCALE_ID,
      useValue: 'pl-PL'
    },
    providePrimeNG({
      translation: {
        startsWith: 'Zaczyna się od',
        contains: 'Zawiera',
        notContains: 'Nie zawiera',
        endsWith: 'Kończy się na',
        equals: 'Równa się',
        notEquals: 'Nie równa się',
        noFilter: 'Brak filtra',
        lt: 'Mniejsze niż',
        lte: 'Mniejsze lub równe',
        gt: 'Większe niż',
        gte: 'Większe lub równe',
        is: 'Jest',
        isNot: 'Nie jest',
        before: 'Przed',
        after: 'Po',
        dateIs: 'Data jest',
        dateIsNot: 'Data nie jest',
        dateBefore: 'Data przed',
        dateAfter: 'Data po',
        clear: 'Wyczyść',
        apply: 'Zastosuj',
        matchAll: 'Dopasuj wszystko',
        matchAny: 'Dopasuj dowolne',
        addRule: 'Dodaj regułę',
        removeRule: 'Usuń regułę',
        accept: 'Akceptuj',
        reject: 'Odrzuć',
        choose: 'Wybierz',
        upload: 'Prześlij',
        cancel: 'Anuluj',
        fileSizeTypes: [],
        dayNames: ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'],
        dayNamesShort: ['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So'],
        dayNamesMin: ['N', 'P', 'W', 'Ś', 'C', 'P', 'S'],
        monthNames: [
          'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
          'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
        ],
        monthNamesShort: [
          'Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze',
          'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'
        ],
        dateFormat: 'dd/mm/yy',
        firstDayOfWeek: 1,
        today: 'Dziś',
        weekHeader: 'Tydz.',
        weak: 'Słabe',
        medium: 'Średnie',
        strong: 'Mocne',
        passwordPrompt: 'Wpisz hasło',
        emptyMessage: 'Brak wyników',
        emptyFilterMessage: 'Brak wyników dla filtra',
        fileChosenMessage: 'Wybrano plik',
        noFileChosenMessage: 'Nie wybrano pliku',
        pending: 'Oczekuje',
        chooseYear: 'Wybierz rok',
        chooseMonth: 'Wybierz miesiąc',
        chooseDate: 'Wybierz datę',
        prevDecade: 'Poprzednia dekada',
        nextDecade: 'Następna dekada',
        prevYear: 'Poprzedni rok',
        nextYear: 'Następny rok',
        prevMonth: 'Poprzedni miesiąc',
        nextMonth: 'Następny miesiąc',
        prevHour: 'Poprzednia godzina',
        nextHour: 'Następna godzina',
        prevMinute: 'Poprzednia minuta',
        nextMinute: 'Następna minuta',
        prevSecond: 'Poprzednia sekunda',
        nextSecond: 'Następna sekunda',
        am: 'AM',
        pm: 'PM',
        searchMessage: 'Wyszukaj...',
        selectionMessage: 'Wybrano {0} elementów',
        emptySelectionMessage: 'Brak wybranych elementów',
        emptySearchMessage: 'Brak wyników wyszukiwania'
      },
      theme: {
        preset: ekowitrynaPreset,
        options: {
          darkModeSelector: '.dark',
          cssLayer: false
        }
      }
    }),
    ...provideEkoPreferencesSync(),
    provideEkoBrowserTitle('app.browserTitle'),
  ] 
})
export class AppModule { }

